import React, { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

// Hidden-file-input + label-button pair used in the Photographers toolbar.
// Split out so the page header is just markup and orchestration.
export function ImportPhotographersButton({ onImported }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/import/photographers`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      const parts = [];
      if (data.created) parts.push(`${data.created} created`);
      if (data.updated) parts.push(`${data.updated} updated`);
      if (data.skipped?.length) parts.push(`${data.skipped.length} skipped`);
      const summary = parts.length ? parts.join(", ") : "no changes";
      if (data.skipped?.length) {
        toast.warning(`CSV import: ${summary}`, {
          description: "See the panel for the skipped rows and reasons.",
          duration: 6000,
        });
      } else {
        toast.success(`CSV import: ${summary}`);
      }
      await onImported();
    } catch (ex) {
      const msg = formatApiError(ex.response?.data?.detail) || ex.message;
      setResult({ error: msg });
      toast.error(`Import failed: ${msg}`);
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div className="relative">
      <input
        ref={ref} type="file" accept=".csv,text/csv"
        onChange={handleFile} className="hidden"
        data-testid="import-photographers-input"
      />
      <button
        type="button" onClick={() => ref.current?.click()}
        disabled={busy} className="ccs-btn-ghost flex items-center gap-2"
        data-testid="import-photographers-button"
        title="Upload a CSV to add or update photographers — click Format help for the two accepted layouts"
      >
        <Upload size={16} /> {busy ? "Importing…" : "Import CSV"}
      </button>
      {result && (
        <div
          className="absolute right-0 mt-2 z-30 text-xs p-3"
          style={{ background: "#1a1a1a", border: "1px solid #3f3f3f", minWidth: 260 }}
        >
          {result.error ? (
            <div className="danger-text">{result.error}</div>
          ) : (
            <>
              <div className="success-text">
                Created {result.created}, Updated {result.updated}
                {typeof result.unchanged === "number" && (
                  <span className="muted-text">, Unchanged {result.unchanged}</span>
                )}
              </div>
              {result.updated === 0 && result.created === 0 && (result.unchanged ?? 0) > 0 && (
                <div className="muted-text mt-1">
                  All {result.unchanged} row{result.unchanged === 1 ? "" : "s"} matched an
                  existing photographer with identical values — nothing was overwritten.
                </div>
              )}
              {result.skipped?.length > 0 && (
                <div className="muted-text mt-2">
                  Skipped {result.skipped.length}:
                  <ul className="list-disc pl-4 mt-1 max-h-32 overflow-y-auto">
                    {result.skipped.slice(0, 10).map((s, i) => (
                      <li key={`${s.line}-${i}`}>L{s.line}: {s.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
              <button onClick={() => setResult(null)} className="text-amber-400 mt-2">close</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
